import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = new Set();

  get gallery() {
    return this.refs.media?.[0]?.parentElement || null;
  }

 connectedCallback() {
  super.connectedCallback();

  const gallery = this.gallery;

  if (gallery) {
    gallery.addEventListener('scroll', this.handleScroll, {
      passive: true,
    });
  }

  this.createSliderArrows();
}
createSliderArrows() {
  const { dialog } = this.refs;

  if (!dialog) return;

  /* Prevent duplicate arrows */
  if (
    dialog.querySelector('.zoom-slider-arrow')
  ) {
    return;
  }

  /* Previous button */
  const previousButton =
    document.createElement('button');

  previousButton.type = 'button';

  previousButton.className =
    'zoom-slider-arrow zoom-slider-arrow--prev';

  previousButton.setAttribute(
    'aria-label',
    'Previous image'
  );

  previousButton.innerHTML = `
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M15 18L9 12L15 6"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  `;

  /* Next button */
  const nextButton =
    document.createElement('button');

  nextButton.type = 'button';

  nextButton.className =
    'zoom-slider-arrow zoom-slider-arrow--next';

  nextButton.setAttribute(
    'aria-label',
    'Next image'
  );

  nextButton.innerHTML = `
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M9 18L15 12L9 6"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  `;

  /* Previous click */
  previousButton.addEventListener(
    'click',
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      this.goToPrevious();
    }
  );

  /* Next click */
  nextButton.addEventListener(
    'click',
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      this.goToNext();
    }
  );

  dialog.appendChild(previousButton);
  dialog.appendChild(nextButton);

  this.updateArrowState();
}

  disconnectedCallback() {
    const gallery = this.gallery;

    if (gallery) {
      gallery.removeEventListener('scroll', this.handleScroll);
    }

    super.disconnectedCallback();
  }

  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;

    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      targetThumbnail?.scrollIntoView({
        behavior: 'instant',
        block: 'center',
        inline: 'center',
      });

      this.scrollToMedia(index, 'instant');
    };

    const sourceImage =
      event.target instanceof Element
        ? event.target.closest('li,slideshow-slide')
        : null;

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      !sourceImage ||
      !targetImage
    ) {
      return open();
    }

    const itemTransitionName = 'gallery-item-open';

    sourceImage.style.setProperty(
      'view-transition-name',
      itemTransitionName
    );

    const focalPoint = sourceImage.dataset.focalPoint;

    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    await startViewTransition(() => {
      open();

      sourceImage.style.removeProperty('view-transition-name');

      targetImage.style.setProperty(
        'view-transition-name',
        itemTransitionName
      );
    });

    document.documentElement.style.removeProperty(
      '--gallery-media-focal-point'
    );

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, {
      behavior: 'instant',
    });
  }

  loadHighResolutionImage(mediaContainer) {
    if (
      !mediaContainer.classList.contains(
        'product-media-container--image'
      )
    ) {
      return false;
    }

    const image = mediaContainer.querySelector(
      'img.product-media__image'
    );

    if (!(image instanceof HTMLImageElement)) {
      return false;
    }

    const highResolutionUrl =
      image.getAttribute('data_max_resolution');

    if (
      !highResolutionUrl ||
      this.#highResImagesLoaded.has(highResolutionUrl)
    ) {
      return false;
    }

    preloadImage(highResolutionUrl);

    const newImage = new Image();

    newImage.className = image.className;
    newImage.alt = image.alt;

    newImage.setAttribute(
      'data_max_resolution',
      highResolutionUrl
    );

    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);

      this.#highResImagesLoaded.add(
        highResolutionUrl
      );
    };

    newImage.src = highResolutionUrl;
  }

  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const gallery = this.gallery;

    if (!gallery || !media.length) return;

    const mostVisibleElement =
      await getMostVisibleElement(media);

    const activeIndex =
      media.indexOf(mostVisibleElement);

    if (activeIndex < 0) return;

    const targetThumbnail =
      thumbnails.children[activeIndex];

    if (
      !targetThumbnail ||
      !(targetThumbnail instanceof HTMLElement)
    ) {
      return;
    }

    Array.from(
      thumbnails.querySelectorAll('button')
    ).forEach((button, i) => {
      button.setAttribute(
        'aria-selected',
        `${i === activeIndex}`
      );
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });

    this.loadHighResolutionImage(
      mostVisibleElement
    );

    this.dispatchEvent(
      new ZoomMediaSelectedEvent(activeIndex)
    );
    this.updateArrowState();
  }, 50);

  scrollToMedia(index, behavior = 'smooth') {
    const gallery = this.gallery;

    if (!gallery) return;

    const targetImage = this.refs.media[index];

    if (!targetImage) return;

    const galleryRect =
      gallery.getBoundingClientRect();

    const imageRect =
      targetImage.getBoundingClientRect();

    const left =
      gallery.scrollLeft +
      imageRect.left -
      galleryRect.left;

    gallery.scrollTo({
      left,
      top: 0,
      behavior,
    });
  }

  async close() {
    const { dialog, media } = this.refs;

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice()
    ) {
      return this.closeDialog();
    }

    const mostVisibleElement =
      await getMostVisibleElement(media);

    const activeIndex =
      media.indexOf(mostVisibleElement);

    const itemTransitionName =
      'gallery-item-close';

    const mediaGallery =
      this.closest('media-gallery');

    const slideshowActive =
      mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive
      ? mediaGallery?.slideshow?.slides?.[activeIndex]
      : mediaGallery?.media?.[activeIndex];

    if (!slide) {
      return this.closeDialog();
    }

    const focalPoint =
      slide.dataset.focalPoint;

    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(
      this.refs.thumbnails
    );

    mostVisibleElement.style.setProperty(
      'view-transition-name',
      itemTransitionName
    );

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty(
        'view-transition-name'
      );

      slide.style.setProperty(
        'view-transition-name',
        itemTransitionName
      );

      this.closeDialog();
    });

    slide.style.removeProperty(
      'view-transition-name'
    );

    dialog.classList.remove(
      'dialog--closed'
    );

    document.documentElement.style.removeProperty(
      '--gallery-media-focal-point'
    );
  }

  closeDialog() {
    const { dialog } = this.refs;

    dialog.close();

    window.dispatchEvent(
      new DialogCloseEvent()
    );
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goToNext();
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goToPrevious();
    }
  }

  async handleThumbnailClick(index) {
    const behavior =
      prefersReducedMotion()
        ? 'instant'
        : 'smooth';

    this.selectThumbnail(index, {
      behavior,
    });
  }

  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;

    if (!media[index]) return;

    this.loadHighResolutionImage(
      media[index]
    );
  }

  async selectThumbnail(
    index,
    options = { behavior: 'smooth' }
  ) {
    if (
      !this.refs.thumbnails ||
      !this.refs.thumbnails.children.length
    ) {
      return;
    }

    if (
      isNaN(index) ||
      index < 0 ||
      index >= this.refs.thumbnails.children.length
    ) {
      return;
    }

    const {
      media,
      thumbnails,
    } = this.refs;

    const targetThumbnail =
      thumbnails.children[index];

    if (
      !targetThumbnail ||
      !(targetThumbnail instanceof HTMLElement)
    ) {
      return;
    }

    Array.from(
      thumbnails.querySelectorAll('button')
    ).forEach((button, i) => {
      button.setAttribute(
        'aria-selected',
        `${i === index}`
      );
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      this.scrollToMedia(
        index,
        options.behavior
      );

      this.loadHighResolutionImage(
        targetImage
      );
    }

    this.dispatchEvent(
      new ZoomMediaSelectedEvent(index)
    );
  }

goToNext() {
  const total = this.refs.media.length;

  if (!total) return;

  const currentIndex = this.getCurrentIndex();

  const nextIndex =
    currentIndex >= total - 1
      ? 0
      : currentIndex + 1;

  this.selectThumbnail(nextIndex, {
    behavior: prefersReducedMotion()
      ? 'instant'
      : 'smooth',
  });
}

goToPrevious() {
  const total = this.refs.media.length;

  if (!total) return;

  const currentIndex = this.getCurrentIndex();

  const previousIndex =
    currentIndex <= 0
      ? total - 1
      : currentIndex - 1;

  this.selectThumbnail(previousIndex, {
    behavior: prefersReducedMotion()
      ? 'instant'
      : 'smooth',
  });
}
updateArrowState() {
  const previousButton = this.querySelector(
    '.zoom-slider-arrow--prev'
  );

  const nextButton = this.querySelector(
    '.zoom-slider-arrow--next'
  );

  if (previousButton) {
    previousButton.disabled = false;
  }

  if (nextButton) {
    nextButton.disabled = false;
  }
}

  getCurrentIndex() {
    const gallery = this.gallery;

    if (!gallery) return 0;

    const scrollLeft =
      gallery.scrollLeft;

    let closestIndex = 0;
    let closestDistance = Infinity;

    this.refs.media.forEach(
      (media, index) => {
        const distance = Math.abs(
          media.offsetLeft -
          scrollLeft
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      }
    );

    return closestIndex;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define(
    'zoom-dialog',
    ZoomDialog
  );
}

function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer =
      new IntersectionObserver(
        (entries) => {
          const mostVisible =
            entries.reduce(
              (prev, current) =>
                current.intersectionRatio >
                prev.intersectionRatio
                  ? current
                  : prev
            );

          observer.disconnect();

          resolve(
            mostVisible.target
          );
        },
        {
          root: elements[0]?.parentElement || null,
          threshold: Array.from(
            { length: 100 },
            (_, i) => i / 100
          ),
        }
      );

    elements.forEach((element) => {
      observer.observe(element);
    });
  });
}